import { PopupType } from '../../../components/popup/types';
import { Logger } from '../../../logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '../../../logger/RoundIntegrityWatcher';
import { fetchUserInfoAction } from '../../../store/apiActions/fetchUserInfoAction';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { type AutoplaySliceType, AutoplayStatus } from '../../../store/slices/autoplay/types';
import { gameStateActions } from '../../../store/slices/game/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { toastActions } from '../../../store/slices/toast/slice';
import type { GameCommunicationMessageType } from '../types';
import { MessageName } from '../types';

import { BaseCoreGameCommunicationProcessor } from './BaseCoreGameCommunicationProcessor';

export interface WinMessage extends GameCommunicationMessageType {
  // TODO: document me in storybook
  gameId: string;
  mCap: boolean; // Maximum casino payout cap reached
  megawin: string; // is this win a megawin (special win animations)
  nwb?: string; // Net Win Balance
  table: string; // Table id
  win?: string; // Number in a string format
}

export class CoreWinGameCommunicationProcessor extends BaseCoreGameCommunicationProcessor {
  public messageName = MessageName.Win;

  public process(message: WinMessage): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.Win);

    if (message.nwb) {
      this.store.dispatch(gameStateActions.setNetPosition(parseFloat(message.nwb)));
    }
    const win = message?.win ? parseFloat(message.win) : 0;
    const isMegaWin = message?.megawin === 'true';

    this.store.dispatch(gameStateActions.setWinResult({ amount: win, updateTimestamp: Date.now(), isMegaWin }));

    const state = this.store.getState();

    const JSESSIONID = state.config.session?.JSESSIONID;
    const baseUrl = state.config.session?.metaServer;
    if (JSESSIONID && baseUrl) {
      this.store.dispatch(fetchUserInfoAction({ JSESSIONID, force: true, baseUrl, essential: true }));
    }

    const autoplay = state.autoplay;
    const bets = state.bets;
    if (autoplay.isInProgress || (autoplay.autoplayStatus === AutoplayStatus.Paused && !!bets.acceptedBets.length)) {
      const currentPopup = state.popup.currentPopup?.popupName;
      if (currentPopup === 'AutoplayModified') {
        this.store.dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        this.store.dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
      }

      if (!autoplay.isLaunchedMidRound) {
        const netChange = win - bets.totalBet;
        this.checkLimits(win, autoplay, netChange);
        this.store.dispatch(autoplayActions.updateBalanceChange(netChange));
      }

      if (autoplay.roundsLeft <= 0) {
        this.store.dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
        this.store.dispatch(autoplayActions.stop());
        Logger.logEvent('autoplay_stop', { additional: 'Completed' });
      }
    }
  }

  /**
   * Check the game state after the win for limit reaching (autoplay limits, casino cap limit etc.)
   * AUTOPLAY SINGLE WIN LIMIT
   * AUTOPLAY BALANCE INCREASE LIMIT
   * AUTOPLAY BALANCE DECREASE LIMIT
   */
  private readonly checkLimits = (win: number, autoplay: AutoplaySliceType, netChange: number): void => {
    if (
      (autoplay.singleWinLimit && autoplay.singleWinLimit <= win) ||
      (autoplay.balanceIncreaseLimit && autoplay.balanceIncreaseLimit <= netChange) ||
      (autoplay.balanceDecreaseLimit && -autoplay.balanceDecreaseLimit >= netChange)
    ) {
      Logger.logEvent('autoplay_stop', { additional: 'LimitsExceeded' });
      this.stopAutoplay();
    }
  };

  private readonly stopAutoplay = (): void => {
    this.store.dispatch(autoplayActions.stop());
    this.store.dispatch(popupActions.pushPopup(PopupType.AutoplayStopped));
  };
}
